import React from 'react';
import { push } from 'react-router-redux';
import gettext from 'js-utils/gettext';
import { SubmissionError, change } from 'redux-form';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import { join as joinPaths } from 'js-utils/path';
import { setWindowLocation } from 'js-utils/http';
import {
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eb/site-analytics';
import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
} from '@eb/social-login-facebook';
import { parseFormPostData } from '../api/utils';
import { getSetPasswordEvent } from '../utils';
import {
    getLogInFormData,
    getSignUpFormData,
    getSocialLogInFormData,
    getSocialSignUpFormData,
    handleResetPasswordAccountStatusResponse,
    handleAccountStatusResponse,
    handleAccountStatusResponseForLinking,
    shouldOnboardingRedirect,
} from './utils';
import {
    checkAccountStatus as checkAccountStatusApi,
    logInUser as logInUserApi,
    signUpUser as signUpUserApi,
    socialLogIn as socialLogInApi,
    sendResetPasswordEmail as sendResetPasswordEmailApi,
    sendSoftAccountActivationEmail as sendSoftAccountActivationEmailApi,
    setPassword as setPasswordApi,
    loginWithToken as loginWithTokenApi,
    postAddExternalIdentity,
} from '../api';
import {
    RESET_PASSWORD_STATUS_PATH,
    LOG_IN_FORM,
    LINK_ACCOUNTS_LOG_IN_FORM,
    SOFT_ACCOUNT_STATUS_PATH,
    BASE_URL,
    GA_AUTH_CATEGORY,
    GA_CONFIRM_EMAIL_SHOWED,
    GA_RE_ENTER_EMAIL_LABEL,
    GA_EDIT_EMAIL_EVENT,
    GA_EDIT_EMAIL_MISMATCH,
    GA_SIGNUP_EVENT,
    GA_LOGIN_EVENT,
    GA_LOGIN_INVALID_PASSWORD,
    GA_SOCIAL_LOGIN_EVENT,
    GA_SOCIAL_SIGNUP_EVENT,
    GA_LINK_ACCOUNTS_LOGIN_EVENT,
    GA_LOGIN_INSTEAD_CLICKED_EVENT,
    GA_FORGOT_PASSWORD_EVENT,
    GA_CHECK_EMAIL_PROMPT_EVENT,
    GA_EXISTING_ACCT_LABEL,
    GA_SOFT_ACCT_LABEL,
    CAPTCHA_FIELD,
    ARGUMENTS_ERROR,
    EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
    EXTERNAL_USER_UNKNOWN_ERROR,
    EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
    EMAIL_PERMISSION_DENIED_ERROR,
    TOKEN_NOT_AUTHORIZED_ERROR,
    INVALID_SOCIAL_LOGIN_FIELD,
    EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
    TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
    UNKNOWN_ERROR_MESSAGE,
    GA_APPLE_LOGIN_BUTTON_CLICKED_EVENT,
    GA_FB_LOGIN_BUTTON_CLICKED_EVENT,
    GA_FB_LOGIN_NO_EMAIL_ADDRESS,
    GA_FB_LOGIN_EMAIL_NOT_SHARED,
    GA_FB_LOGIN_EMAIL_ALREADY_EXISTS,
    GA_FB_LOGIN_EMAIL_ALREADY_EXISTS_ON_SOFT_ACCOUNT,
    GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB,
    GA_FB_LOGIN_EXTERNAL_USER_UNKNOWN,
    GA_FB_LOGIN_PERMISSIONS_NOT_GRANTED,
    GA_GET_STARTED_CLICKED,
    GA_FB_LOGIN_RETURNING_USER_LOGIN_SUCCESS,
    GA_FB_LOGIN_ACCOUNT_CREATED,
    GA_FB_LOGIN_ACCOUNT_LINKING_FAILED,
    GA_TOS_CLICKED,
    GA_COMUNITY_GUIDELINES_CLICKED,
    GA_PRIVACY_POLICY_CLICKED,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_SIGN_UP_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SUSPENDED_ACCOUNT,
    LOCKED_ACCOUNT_ERROR_MESSAGE,
} from '../constants';

import {
    trackSIWA_WrongPassword,
    trackSIWA_AccountCreatedWithHiddenEmail,
    trackSIWA_errorOnNewUser,
} from './gaTracking';

import AlertChunkySvg from 'eventbrite_design_system/iconography/icons/AlertChunky';

export const INVALID_PASSWORD_RESET_TOKEN_ACTION = 'invalidToken';
export const invalidPasswordResetToken = () => ({
    type: INVALID_PASSWORD_RESET_TOKEN_ACTION,
    payload: true,
});

export const SET_SOCIAL_DATA_ACTION = 'setSocialDataAction';
export const setSocialData = ({
    accessToken,
    sourceProvider,
    email,
    firstName,
    lastName,
}) => ({
    type: SET_SOCIAL_DATA_ACTION,
    payload: {
        accessToken,
        sourceProvider,
        email,
        firstName,
        lastName,
    },
});

export const SET_EMAIL_ACTION = 'setEmail';
export const setEmail = (email) => ({
    type: SET_EMAIL_ACTION,
    payload: email,
});

export const CLEAR_EMAIL_ACTION = 'clearEmail';
export const clearEmail = () => ({
    type: CLEAR_EMAIL_ACTION,
});

export const FORCE_STLD = 'forceStld';
export const forceStld = () => ({
    type: FORCE_STLD,
});

export const SHOW_CAPTCHA = 'showCaptcha';
export const showCaptcha = () => ({
    type: SHOW_CAPTCHA,
});

export const HIDE_CAPTCHA = 'hideCaptcha';
export const hideCaptcha = () => ({
    type: HIDE_CAPTCHA,
});

export const SHOW_AUTH_NOTIFICATION = 'showAuthNotification';
export const showAuthNotification = ({ message, type, iconType }) => ({
    type: SHOW_AUTH_NOTIFICATION,
    payload: {
        message,
        type,
        iconType,
    },
});

export const CLEAR_AUTH_NOTIFICATION = 'clearAuthNotification';
export const clearAuthNotification = () => ({
    type: CLEAR_AUTH_NOTIFICATION,
});

export const SET_ACCOUNT_STATUS_CHECKED = 'setAccountStatusChecked';
export const setAccountStatusChecked = (email) => ({
    type: SET_ACCOUNT_STATUS_CHECKED,
    payload: email,
});

export const SHOW_LINKED_ACCOUNTS_DIALOG = 'showLinkedAccountsDialog';
export const showLinkedAccountsDialog = (redirectUrl) => ({
    type: SHOW_LINKED_ACCOUNTS_DIALOG,
    payload: {
        redirectUrl,
    },
});

export const RE_REQUEST_PERMISSIONS = 'setReRequestPermissionStatus';
export const setReRequestPermissionStatus = () => ({
    type: RE_REQUEST_PERMISSIONS,
    payload: true,
});

export const CLEAN_SOCIAL_AUTHENTICATION_DATA_ACTION =
    'cleanSocialAuthenticationDataAction';
export const cleanSocialAuthenticationDataAction = () => ({
    type: CLEAN_SOCIAL_AUTHENTICATION_DATA_ACTION,
});
export const ADD_SOCIAL_AUTHENTICATION_DATA_ACTION =
    'addSocialAuthenticationDataAction';
export const addSocialAuthenticationDataAction = ({
    idToken,
    isEmailHidden,
    isUser,
    expirationTimestamp,
    email,
    externalIdentities,
}) => ({
    type: ADD_SOCIAL_AUTHENTICATION_DATA_ACTION,
    payload: {
        socialAuthenticationData: {
            idToken,
            isEmailHidden,
            isUser,
            expirationTimestamp,
            email,
            externalIdentities,
        },
    },
});

export const SIWA_TIMER_TIMEOUT = 'SIWA_TIMER_TIMEOUT';
export const timeoutSIWATimer = ({ message, type, iconType }) => ({
    type: SIWA_TIMER_TIMEOUT,
    payload: {
        message,
        type,
        iconType,
    },
});
export const SIWA_TIMER_TICK = 'SIWA_TIMER_TICK';
export const tickSIWATimer = () => ({
    type: SIWA_TIMER_TICK,
});

export const updateFormType = (path = '') => (dispatch) => {
    //Any time we change form want to reset our captcha display logic.
    //Decision service will decide if it will be presented on submission.
    dispatch(hideCaptcha());
    dispatch(push(joinPaths(BASE_URL, path)));
};

export const cleanSocialAuthenticationData = () => (dispatch) => {
    //Any time we want to reset the social authentication data we dispatch this action.
    dispatch(cleanSocialAuthenticationDataAction());
};

export const getEmailFromState = (getState) => {
    const {
        authentication: {
            email,
            socialAuthenticationData: { email: _socialEmail } = {},
        } = {},
    } = getState();

    return email || _socialEmail;
};

// eslint-disable-next-line
const _handleOnError = (dispatch, state, data) => (error) => {
    if (error.name === 'CAPTCHA_REQUIRED') {
        //we need to dispatch an action to handle displaying the captcha, so this specific error
        //lives slightly outside of the standard error protocol. Have it here because proxying the
        //specific error for requiring the captcha to this point is cleaner than inserting dispatch
        //directly into the response handling itself
        dispatch(showCaptcha());

        //once we are here in the response handling and have access to dispatch, we need to continue
        //passing along the error to redux form such that its lifecycle methods correctly represent the
        //failed submission state. We don't want to actually display a form error, as at this stage
        //showing the captcha
        throw new SubmissionError({
            [CAPTCHA_FIELD]: gettext('Complete to proceed'),
        });
    }

    if (error.name === EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        const {
            authentication: { socialData },
        } = state;

        // Merge current social data with possible changes made by user
        const updatedSocialData = { ...socialData, ...data };

        dispatch(
            showAuthNotification({
                message: LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL,
                iconType: <AlertChunkySvg />,
            }),
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EMAIL_ALREADY_EXISTS,
            category: GA_AUTH_CATEGORY,
        });

        /*
        The subsequent routes (login/signup/etc) expect email to be in state, and the
        account status to be checked, so need to update store before dealing with the response
        logic. When setting account status as checked, the email reducer is
        also listening and will update.
        */
        if (updatedSocialData && updatedSocialData.email) {
            dispatch(setAccountStatusChecked(updatedSocialData.email));
        }
        if (updatedSocialData) {
            dispatch(setSocialData(updatedSocialData));
        }

        dispatch(updateFormType(LINK_ACCOUNTS_LOG_IN_PATH));
        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR) {
        dispatch(
            showAuthNotification({
                message: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EMAIL_ALREADY_EXISTS_ON_SOFT_ACCOUNT,
            category: GA_AUTH_CATEGORY,
        });

        if (data && data.email) {
            dispatch(setAccountStatusChecked(data.email));
        }
        dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === EXTERNAL_USER_WITHOUT_EMAIL_ERROR) {
        // dispatch action to redirect to social sign up
        // using data brought from external source
        if (data && (data.firstName || data.lastName)) {
            dispatch(setSocialData(data));
        }

        dispatch(updateFormType(SOCIAL_SIGN_UP_PATH));

        trackEventFromState(state, {
            action: GA_FB_LOGIN_NO_EMAIL_ADDRESS,
            category: GA_AUTH_CATEGORY,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === EMAIL_PERMISSION_DENIED_ERROR) {
        // dispatch action that makese the FacebookLoginComponent re request permissions.
        dispatch(setReRequestPermissionStatus());

        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EMAIL_NOT_SHARED,
            category: GA_AUTH_CATEGORY,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === EXTERNAL_USER_UNKNOWN_ERROR) {
        // dispatch action to redirect to social sign up
        // using data brought from external source
        let formType = SOCIAL_SIGN_UP_PATH;
        let message = null;

        checkAccountStatusApi(data.email).then(
            ({ exists, can_login: canLogin }) => {
                // If user exists, skip confirmation view
                if (exists) {
                    // If user has full account, redirect to link accounts view
                    if (canLogin) {
                        message = LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL;
                        formType = LINK_ACCOUNTS_LOG_IN_PATH;
                        // If user has soft account, redirect to create password view
                    } else {
                        message = EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE;
                        formType = SET_LINKED_SOFT_ACCOUNTS_PATH;
                    }
                    dispatch(setAccountStatusChecked(data.email));
                    dispatch(
                        showAuthNotification({
                            message,
                            iconType: <AlertChunkySvg />,
                        }),
                    );
                }

                dispatch(setSocialData(data));
                dispatch(updateFormType(formType));
            },
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EXTERNAL_USER_UNKNOWN,
            category: GA_AUTH_CATEGORY,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === TOKEN_NOT_AUTHORIZED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === INVALID_SOCIAL_LOGIN_FIELD) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        // it shows the unknown error message
        dispatch(
            showAuthNotification({
                message: UNKNOWN_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === SUSPENDED_ACCOUNT) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        // it shows the unknown error message
        dispatch(
            showAuthNotification({
                message: LOCKED_ACCOUNT_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === ARGUMENTS_ERROR) {
        dispatch(
            showAuthNotification({
                message: UNKNOWN_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );

        return Promise.resolve();
    }

    if (error.errors && error.errors.password) {
        if (isEmpty(state.authentication.socialAuthenticationData)) {
            trackEventFromState(state, {
                action: GA_LOGIN_INVALID_PASSWORD,
                category: GA_AUTH_CATEGORY,
            });
        }

        // state.authentication.socialAuthenticationData is not empty when is SIWA
        else {
            trackSIWA_WrongPassword(state);
        }

        // throw the error because is related with
        // the password form field
        return Promise.reject(error);
    }

    // Unknown error, let the user know and reject the promise
    dispatch(
        showAuthNotification({
            message: UNKNOWN_ERROR_MESSAGE,
            iconType: <AlertChunkySvg />,
        }),
    );

    return Promise.reject(error);
};

const submitSuccessRedirect = (url) => () => setWindowLocation(url);

const handleSocialLoginSuccessResponse = (state, response) => {
    if (response['new_user']) {
        trackEventFromState(state, {
            action: GA_FB_LOGIN_ACCOUNT_CREATED,
            category: GA_AUTH_CATEGORY,
        });
    } else {
        trackEventFromState(state, {
            action: GA_FB_LOGIN_RETURNING_USER_LOGIN_SUCCESS,
            category: GA_AUTH_CATEGORY,
        });
    }
};

export const sendSoftAccountActivationEmail = () => (dispatch, getState) => {
    const state = getState();
    const { authentication: auth } = state;

    return sendSoftAccountActivationEmailApi(auth.email, auth.referrer)
        .then(() =>
            dispatch(
                push(joinPaths(BASE_URL, 'status', SOFT_ACCOUNT_STATUS_PATH)),
            ),
        )
        .then(() =>
            trackEventFromState(state, {
                action: GA_CHECK_EMAIL_PROMPT_EVENT,
                category: GA_AUTH_CATEGORY,
                label: GA_SOFT_ACCT_LABEL,
            }),
        );
};

export const moveBack = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: { pathname },
        },
    } = state;

    if (pathname === `${BASE_URL}/${SOCIAL_LOG_IN_PATH}`) {
        dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));
    } else {
        trackEventFromState(state, {
            action: GA_EDIT_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
        });

        dispatch(cleanSocialAuthenticationData());
        dispatch(updateFormType());
    }
};

export const sendResetPasswordEmail = () => (dispatch, getState) => {
    const state = getState();

    trackEventFromState(state, {
        action: GA_FORGOT_PASSWORD_EVENT,
        category: GA_AUTH_CATEGORY,
    });

    return sendResetPasswordEmailApi(getEmailFromState(getState))
        .then(() =>
            dispatch(
                push(joinPaths(BASE_URL, 'status', RESET_PASSWORD_STATUS_PATH)),
            ),
        )
        .then(() =>
            trackEventFromState(state, {
                action: GA_CHECK_EMAIL_PROMPT_EVENT,
                category: GA_AUTH_CATEGORY,
                label: GA_EXISTING_ACCT_LABEL,
            }),
        );
};

export const resetPassword = ({ email }) => (dispatch) => {
    dispatch(setEmail(email));

    return checkAccountStatusApi(email).then((response) =>
        handleResetPasswordAccountStatusResponse(dispatch, response),
    );
};

export const accountStatusCheck = ({ email }) => (dispatch, getState) =>
    checkAccountStatusApi(email).then((response) =>
        handleAccountStatusResponse(response, dispatch, getState, email),
    );
export const accountStatusCheckForLinking = ({ email }) => (
    dispatch,
    getState,
) =>
    checkAccountStatusApi(email).then((response) =>
        handleAccountStatusResponseForLinking(
            response,
            dispatch,
            getState,
            email,
        ),
    );

export const logInUser = (data) => (dispatch, getState) => {
    const state = getState();

    dispatch(hideCaptcha());
    dispatch(change(LOG_IN_FORM, CAPTCHA_FIELD, null));

    return logInUserApi(parseFormPostData(getLogInFormData(data, getState)))
        .then((response) =>
            dispatch(submitSuccessRedirect(response['redirect_url'])),
        )
        .then(() =>
            trackEventFromStateOnPageUnload(state, {
                action: GA_LOGIN_EVENT,
                category: GA_AUTH_CATEGORY,
            }),
        )
        .catch(_handleOnError(dispatch, state));
};

export const signUpUser = (data) => (dispatch, getState) => {
    const state = getState();

    return signUpUserApi(parseFormPostData(getSignUpFormData(data, getState)))
        .then((response) => {
            let redirectUrl = response['redirect_url'];

            if (shouldOnboardingRedirect(state)) {
                redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
            }
            return dispatch(submitSuccessRedirect(redirectUrl));
        })
        .then(() =>
            trackEventFromStateOnPageUnload(state, {
                action: GA_SIGNUP_EVENT,
                category: GA_AUTH_CATEGORY,
            }),
        )
        .catch(_handleOnError(dispatch, state));
};

export const socialLogInUser = (socialProvider, data) => (
    dispatch,
    getState,
) => {
    const state = getState();

    return (
        socialLogInApi(
            parseFormPostData(getSocialLogInFormData(data, getState)),
        )
            .then((response) => {
                handleSocialLoginSuccessResponse(state, response);
                let redirectUrl = response['redirect_url'];

                if (response['new_user'] && shouldOnboardingRedirect(state)) {
                    redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
                }

                dispatch(submitSuccessRedirect(redirectUrl));
            })
            .then(() =>
                trackEventFromStateOnPageUnload(state, {
                    action: GA_SOCIAL_LOGIN_EVENT,
                    category: GA_AUTH_CATEGORY,
                }),
            )
            // pass returned data so it can be read and used to make decisions
            .catch(_handleOnError(dispatch, state, data))
    );
};

/* Needed for SIWA, returning to /signin when the email belongs to a soft
 * account there is not a real api call that fails, but we need to trigger
 * the same `onError` that Facebook returns in the same scenario.
 */
export const socialLogInSoftUser = () => (dispatch, getState) => {
    const state = getState();

    _handleOnError(
        dispatch,
        state,
    )({ name: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR });
};

export const socialLogInUserNotAuthorized = (socialProvider, data) => (
    dispatch,
    getState,
) => {
    const state = getState();

    if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK) {
        if (data && includes(['unknown', 'not_authorized'], data.status)) {
            trackEventFromState(state, {
                action: GA_FB_LOGIN_PERMISSIONS_NOT_GRANTED,
                category: GA_AUTH_CATEGORY,
            });
        }
    }
};

export const socialSignUpUser = (data) => (dispatch, getState) => {
    const state = getState();

    return (
        socialLogInApi(parseFormPostData(getSocialSignUpFormData(data, state)))
            .then((response) => {
                handleSocialLoginSuccessResponse(state, response);
                let redirectUrl = response['redirect_url'];

                if (shouldOnboardingRedirect(state)) {
                    redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
                }
                return dispatch(submitSuccessRedirect(redirectUrl));
            })
            .then(() =>
                trackEventFromStateOnPageUnload(state, {
                    action: GA_SOCIAL_SIGNUP_EVENT,
                    category: GA_AUTH_CATEGORY,
                }),
            )
            // pass returned data so it can be read and used to make decisions
            .catch(_handleOnError(dispatch, state, data))
    );
};

/**
 * Called when:
 *  you are linkin your account at the same time you are logging in
 */
export const linkAccountsOnLogin = (data) => (dispatch, getState) => {
    const state = getState();
    let {
        authentication: {
            socialData: { accessToken, sourceProvider },
            socialAuthenticationData: { idToken } = {},
        },
    } = state;

    //If we are in the apple flow we re assign this variables.
    if (idToken) {
        sourceProvider = SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE;
        accessToken = idToken;
    }
    dispatch(hideCaptcha());
    dispatch(change(LINK_ACCOUNTS_LOG_IN_FORM, CAPTCHA_FIELD, null));

    return logInUserApi(parseFormPostData(getLogInFormData(data, getState)))
        .then((response) =>
            postAddExternalIdentity('me', sourceProvider, accessToken)
                .then(() => {
                    if (global.window && window.localStorage) {
                        localStorage.removeItem('referrer');
                    }
                    dispatch(
                        showLinkedAccountsDialog(response['redirect_url']),
                    );

                    if (idToken && response && response['redirect_url']) {
                        dispatch(
                            submitSuccessRedirect(response['redirect_url']),
                        );
                    }
                })
                .catch((error) => {
                    dispatch(submitSuccessRedirect(response['redirect_url']));
                    trackEventFromStateOnPageUnload(getState(), {
                        action: GA_FB_LOGIN_ACCOUNT_LINKING_FAILED,
                        category: GA_AUTH_CATEGORY,
                        label: get(
                            error,
                            'extraData.parsedError.error',
                            get(
                                error,
                                'extraData.response.statusText',
                                'Error not defined',
                            ),
                        ),
                    });
                }),
        )
        .then(() =>
            trackEventFromStateOnPageUnload(state, {
                action: GA_LINK_ACCOUNTS_LOGIN_EVENT,
                category: GA_AUTH_CATEGORY,
                label: sourceProvider,
            }),
        )
        .catch(_handleOnError(dispatch, state));
};

export const linkAccountOnNewUser = (data) => (dispatch, getState) => {
    const state = getState();

    return loginWithTokenApi(data)
        .then((response) => {
            let redirectUrl = '/';

            if (global.window && window.localStorage) {
                redirectUrl = localStorage.getItem('referrer');
                localStorage.removeItem('referrer');
            }

            if (shouldOnboardingRedirect(state)) {
                redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
            }
            dispatch(trackSIWA_AccountCreatedWithHiddenEmail());

            return dispatch(submitSuccessRedirect(redirectUrl));
        })
        .catch((response) => {
            trackSIWA_errorOnNewUser(state, response.description);

            _handleOnError(
                dispatch,
                state,
            )({ name: response.error, message: response.description });
        });
};

export const setPassword = (data) => (dispatch, getState) => {
    const state = getState();
    const currentRoute = state.routing.locationBeforeTransitions.pathname;
    const setPasswordEvent = getSetPasswordEvent(currentRoute);

    return setPasswordApi(data)
        .then((response) =>
            dispatch(submitSuccessRedirect(response['redirect_url'])),
        )
        .then(() =>
            trackEventFromStateOnPageUnload(state, {
                action: setPasswordEvent,
                category: GA_AUTH_CATEGORY,
            }),
        );
};

export const EXISTING_ACCOUNT_CHECK = 'existingAccountCheckAction';
export const existingAccountCheck = (data) => (dispatch) => {
    dispatch({
        type: 'existingAccountCheckAction',
        payload: data,
    });
};

export const trackSocialProviderClick = (socialProvider, stateFromProvider) => (
    dispatch,
    getState,
) => {
    const state = getState();

    if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK) {
        trackEventFromState(state, {
            action: GA_FB_LOGIN_BUTTON_CLICKED_EVENT,
            category: GA_AUTH_CATEGORY,
        });

        if (stateFromProvider && stateFromProvider.status === 'unknown') {
            trackEventFromState(state, {
                action: GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB,
                category: GA_AUTH_CATEGORY,
            });
        }
    } else if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE) {
        trackEventFromState(state, {
            action: GA_APPLE_LOGIN_BUTTON_CLICKED_EVENT,
            category: GA_AUTH_CATEGORY,
        });
    }
};

export const trackEmailConfirmationInput = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_CONFIRM_EMAIL_SHOWED,
        category: GA_AUTH_CATEGORY,
        label: GA_RE_ENTER_EMAIL_LABEL,
    });
};

export const trackEmailMismatchAction = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_EDIT_EMAIL_MISMATCH,
        category: GA_AUTH_CATEGORY,
    });
};

export const trackGetStartedClick = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_GET_STARTED_CLICKED,
        category: GA_AUTH_CATEGORY,
    });
};

export const trackLoginInsteadClick = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_LOGIN_INSTEAD_CLICKED_EVENT,
        category: GA_AUTH_CATEGORY,
    });
    dispatch(updateFormType());
};

export const trackTermsOfServiceClick = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_TOS_CLICKED,
        category: GA_AUTH_CATEGORY,
    });
};

export const trackComunityGuidelinesClick = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_COMUNITY_GUIDELINES_CLICKED,
        category: GA_AUTH_CATEGORY,
    });
};

export const trackPrivacyPolicyClick = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_PRIVACY_POLICY_CLICKED,
        category: GA_AUTH_CATEGORY,
    });
};

/**
 * Function to trick the app and make set it in the state after a call to an external provider
 * @param {string} url
 * @param {object: {skipTo, idToken, isEmailHidden, isUser, expirationTimestamp, email} } authenticationData
 */
export const onSkipSocialLinks = ({ url, authenticationData }) => (
    dispatch,
) => {
    dispatch(setAccountStatusChecked(''));
    dispatch(addSocialAuthenticationDataAction(authenticationData));
    /**
     * To actually redirect the path should change and to allow a refresh on the current page
     * we push another route to the browser history so it detects the one we want as a NEW.
     * This generates the redirect correctly.
     * NOTE: this won't work if the path where you are using this function is the same as the first
     * push we have down below.
     */
    dispatch(push(joinPaths(BASE_URL, 'signup')));
    dispatch(push(joinPaths(BASE_URL, url)));
};
